import React, { useCallback, useMemo, useRef, useState } from "react";
import { connect } from "react-redux";
import { createStore } from 'devextreme-aspnet-data-nojquery';
import DataSource from "devextreme/data/data_source";
import data from "../../../../assets/apiRoutes.json";
import { GridLayout } from "../../../../common/GridLayout";
import GridLayoutManager from "../../../../gridLayoutCustomization/GridLayoutManager";
import { GRID_PAGE_SIZES, CURRENCY_FORMAT, DEFAULT_LAYOUT_NAME } from "../constants";
import {
    Column,
    DataGrid,
    GroupPanel,
    Grouping,
    FilterRow,
    FilterPanel,
    SearchPanel,
    Sorting,
    Paging,
    Selection,
    Toolbar,
    Item,
    Export,
    Pager,
    ColumnChooser,
    ColumnChooserSearch,
    StateStoring,
} from "devextreme-react/data-grid";

/**
 * Collection credit / follow-up history for one customer
 * (ar/CollectionCreditHeader/GetCollectionCreditHistory), mirroring
 * grdOpenFollowUpTop in OMD0010. Distinct from GetCollectionCreditHeaders,
 * which is the AR Credit/Collections Follow-up screen's own one-row-per-header list.
 */
const CreditHistoryTab = (props) => {
    const { customerId, gridKey } = props;
    const accessToken = props.oidc.user.access_token;
    const dataGrid = useRef(null);

    // Named grid layouts: GridLayoutManager switches currentLayout, and the state
    // storage requests carry it so each layout keeps its own saved state.
    const [currentLayout, setCurrentLayout] = useState(DEFAULT_LAYOUT_NAME);
    const loadState = useCallback(() => GridLayout.sendStorageRequest(gridKey, "GET", accessToken, null, currentLayout), [gridKey, currentLayout]);
    const saveState = useCallback((state) => GridLayout.sendStorageRequest(gridKey, "POST", accessToken, state, currentLayout), [gridKey, currentLayout]);

    const dataSource = useMemo(() => {
        if (!customerId) {
            return [];
        }
        const store = createStore({
            // A header can fan out into several detail rows, and a header with no
            // detail rows still returns one row, so the server supplies GridKey
            // (detail rowid, falling back to the header rowid).
            key: 'GridKey',
            loadParams: { CustomerRowId: customerId },
            loadUrl: `${window._env_.REACT_APP_APIS}/${data.CollectionCreditHeader.GetCollectionCreditHistory}`,
            onBeforeSend: (method, ajaxOptions) => {
                ajaxOptions.headers = {
                    access_token: accessToken,
                    Authorization: `Bearer ${accessToken}`,
                };
            },
        });
        const source = new DataSource({ store: store });
        // Matches qryARCollectionCreditHistory: ORDER BY
        // ARCOLLECTIONCREDITDetail.NextFollowupDate, ARCOLLECTIONCREDITHEADER.Status
        source.sort([
            { selector: 'NextFollowUpDate', desc: false },
            { selector: 'Status', desc: false },
        ]);
        return source;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [customerId]);

    return (
        <DataGrid
            onExporting={(e) => {
                if (window.handleDevExtremeDataGridExport) {
                    window.handleDevExtremeDataGridExport(e);
                }
            }}
            ref={dataGrid}
            className="tabbed-detail-grid"
            dataSource={dataSource}
            keyExpr="GridKey"
            allowColumnReordering={true}
            allowColumnResizing={true}
            showBorders={true}
            hoverStateEnabled={true}
            columnAutoWidth={true}
            remoteOperations={true}
        >
            <ColumnChooser enabled={true} title="Choose Column" width={290} height={290} sortOrder="asc">
                <ColumnChooserSearch enabled={true} editorOptions={{ placeholder: 'Search column' }} />
            </ColumnChooser>
            <Export enabled={true} allowExportSelectedData={true} />
            <Selection mode="single" />
            <Sorting visible={true} mode="multiple" />
            <Paging defaultPageSize={10} />
            <Pager visible={true} showPageSizeSelector={true} allowedPageSizes={GRID_PAGE_SIZES} showNavigationButtons={true} />
            <FilterRow visible={true} />
            <FilterPanel visible={true} />
            <SearchPanel visible={true} width={240} placeholder="Search..." />
            <GroupPanel visible={true} />
            <Grouping autoExpandAll={false} />
            <StateStoring enabled={true} type="custom" customLoad={loadState} storageKey={gridKey} customSave={saveState} />

            {/* Mirrors grdOpenFollowUpTop */}
            <Column dataField="RecordNumber" caption="Record #" />
            <Column dataField="Reference" caption="Reference" />
            <Column dataField="TypeDescription" caption="Type" />
            <Column dataField="InitialCallDate" caption="Initial Call Date" dataType="date" />
            <Column dataField="ContactDateTime" caption="Contact Date" dataType="datetime" />
            <Column dataField="IdentityId" caption="Contacted" />
            <Column dataField="Name" caption="Name" />
            <Column dataField="PromisedAmount" caption="Promised Amount" dataType="number" format={CURRENCY_FORMAT} />
            <Column dataField="PromisedDate" caption="Promised Date" dataType="date" />
            <Column dataField="StatusDescription" caption="Status" />
            <Column dataField="NextFollowUpDate" caption="Next Followup Date" dataType="date" />

            {/* Column chooser (hidden by default) */}
            <Column dataField="Description" caption="Description" visible={false} />
            <Column dataField="Comment" caption="Comment" visible={false} />
            <Column dataField="CreationUserId" caption="Created By" visible={false} />
            <Column dataField="CreationDateTime" caption="Creation Date" dataType="datetime" visible={false} />
            <Column dataField="LastUpdateUserId" caption="Last Updated By" visible={false} />
            <Column dataField="LastUpdateDateTime" caption="Last Updated" dataType="datetime" visible={false} />

            {/* Remaining legacy query fields (uOMM0010) - hidden, available
                through the column chooser like the AI8 grid. */}
            <Column dataField="CompanyId" caption="Company Id" visible={false} />
            <Column dataField="KeySequence" caption="Key Sequence" dataType="number" visible={false} />
            <Column dataField="Module" caption="Module" visible={false} />
            <Column dataField="RecordType" caption="Record Type" visible={false} />

            <Column dataField="DetailKeySequence" caption="Detail Key Sequence" dataType="number" visible={false} showInColumnChooser={false} />

            {/* Technical columns stay hidden and out of the chooser */}
            <Column dataField="GridKey" caption="Grid Key" visible={false} showInColumnChooser={false} />
            <Column dataField="RowId" caption="Row Id" visible={false} showInColumnChooser={false} />
            <Column dataField="DetailRowId" caption="Detail Row Id" visible={false} showInColumnChooser={false} />
            <Column dataField="RCustomer" caption="Customer Id" visible={false} showInColumnChooser={false} />
            <Column dataField="Status" caption="Status Flag" visible={false} showInColumnChooser={false} />
            <Column dataField="Type" caption="Type Flag" visible={false} showInColumnChooser={false} />
            <Toolbar>
                <Item location="after">
                    <GridLayoutManager gridKey={gridKey} dataGrid={dataGrid} setCurrentLayout={setCurrentLayout} />
                </Item>
                <Item name="exportButton" />
                <Item name="columnChooserButton" />
            </Toolbar>
        </DataGrid>
    );
};

function mapStateToProps(state) {
    return {
        oidc: state.oidc,
    };
}
export default connect(mapStateToProps)(CreditHistoryTab);
